import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  DYNAMIC_ONBOARDING_FLOW_KEYS,
  fetchDynamicFlows,
  startOrResumeSession,
  transitionSession,
} from '../api/dynamicAssessments';
import type { DynamicFlowItem, DynamicSession, DynamicUiPayload } from '../types/dynamicAssessment';

import { useDynamicAssessmentAnalytics } from './useDynamicAssessmentAnalytics';
import { useDynamicAssessmentApiClient } from './useDynamicAssessmentApiClient';

/** Normalize an unknown thrown value to a string for Datadog log context. */
function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

export type DynamicAssessmentContent = {
  flowKey: string;
  sessionUuid: string;
  /**
   * The server's optimistic-lock counter (`version` on the wire). Pass as
   * `expectedVersion` to `transitionSession` to guard against concurrent updates.
   */
  sessionVersion: number;
  uiPayload: DynamicUiPayload;
};

/**
 * A screener the member answered, carrying the session it was answered in so the
 * completion event can join to its `dynamic-assessment-screener-started-*` counterpart.
 */
export type AnsweredFlow = {
  flowKey: string;
  /** The session whose question was shown — the same one the answer transitions. */
  sessionUuid: string;
};

type UseDynamicAssessmentSession = {
  /** Populated when the current flow has a question node to render. */
  content: DynamicAssessmentContent | undefined;
  /** True while loading the initial flow / re-querying `/flows/` between flows — the screen holds a full spinner. */
  isLoading: boolean;
  /**
   * True only while the current question's answer is in flight (the `transitionSession`
   * POST). Distinct from `isLoading` so the screen keeps the assessment mounted with a
   * busy Continue button during submit, mirroring the legacy flow, rather than
   * flashing a full-screen spinner on every answer.
   */
  isSubmitting: boolean;
  /** True once all configured flows have been completed or skipped. */
  isExhausted: boolean;
  /**
   * The configured flow keys that completed in this pass (read together with
   * `isExhausted`): the backend's `/flows/` statuses UNIONED with the flows this
   * pass itself drove to a terminal (see `clientCompletedFlowsRef` — the snapshot
   * predates a gate walk's terminal, e.g. phq_gad_v1's score_route). The parent
   * latches it so forward nav skips a standalone screener (pre-diabetes / phq-gad)
   * only when its flow actually completed — an unpublished/abandoned flow is in
   * neither source, so its standalone screener still runs rather than being
   * silently dropped.
   */
  completedFlowKeys: readonly string[];
  /**
   * Configured flows THIS MOUNT drove to a terminal after actually rendering a
   * question — i.e. the screeners the member answered, in completion order.
   *
   * Deliberately narrower than `completedFlowKeys` on both axes, because this is
   * a per-screener analytics signal rather than a routing input:
   *   - **This mount only.** A flow completed in an earlier page load comes back
   *     `completed` in the `/flows/` snapshot, so `pickNextFlow` skips it and it
   *     never lands here — a refresh cannot re-emit its completion.
   *   - **A question must have been shown.** A flow whose gate instant-skips it
   *     (member ineligible) reaches a terminal without ever facing the member;
   *     counting that as a completed screener would inflate the per-screener
   *     completion rate with people who were never asked.
   */
  answeredFlows: readonly AnsweredFlow[];
  /**
   * True when this mount picked up an assessment that was already under way —
   * some configured flow was `active` or `completed` in the first `/flows/` read,
   * which only happens if an earlier page load started it.
   *
   * A property OF THE MOUNT, not of any single event: it says "this entry is a
   * continuation", not "this particular screener was seen before". It exists
   * because the funnel's dedupe guards are plain refs, so a page refresh re-fires
   * the start events with no memory of the earlier load; this is what lets a
   * dashboard separate first entries from resumes instead of over-counting starts.
   * False before the first `/flows/` read resolves, and stays false for a fresh
   * entry.
   */
  isResumed: boolean;
  /**
   * Set when a flow's terminal carried a crisis-line outcome (phq_gad_v1's
   * requiresCrisisLine + qualifiedProgramUrns — Sparta's crisis result from the
   * legacy PHQ/GAD POST). Read together with `isExhausted`: the parent routes to
   * the crisis program screen at assessment completion instead of program matching.
   */
  crisisProgramUrns: string[] | undefined;
  error: unknown;
  /**
   * Submit answers for the current question node. `answers` maps each SurveyJS
   * element name (snake_case signal key, e.g. `taking_aoms`) to its value.
   * On success the next flow loads automatically.
   */
  submit: (answers: Record<string, unknown>) => Promise<void>;
  /**
   * Continue past the currently-rendered gate (`content.uiPayload.infoScreen
   * === true`) — sends the SAME ack the auto-advance walk sends for an
   * unflagged gate with the same expression element(s), derived from
   * `content.uiPayload` rather than hard-coded, then behaves exactly like
   * `submit` (next pass, terminal/crisis capture, completion bookkeeping). A
   * no-op when there's no content, mirroring `submit`'s own guard. The caller
   * (DynamicAssessmentFlow) is what decides WHEN to call this — only while
   * `infoScreen` is `true` — this just knows how to ack correctly once asked.
   */
  continueGate: () => Promise<void>;
};

/**
 * A gate node must be auto-transitioned past without meaningful user answers.
 * Two cases:
 *   1. node_type === 'info'  — pure routing page, no elements
 *   2. node_type === 'question' with only `expression` elements — the start
 *      page has a `_gate` expression element for client-side routing hints;
 *      there is no user-facing input to render.
 */
function isGateNode(session: DynamicSession): boolean {
  const payload = session.uiPayload;
  if (!payload) return false;
  if (payload.nodeType === 'info') return true;
  if (payload.nodeType === 'question') {
    const { elements } = payload;
    if (elements.length === 0) return true;
    return elements.every(
      (el) =>
        typeof el === 'object' &&
        el !== null &&
        (el as Record<string, unknown>).type === 'expression'
    );
  }
  return false;
}

/**
 * A gate flagged `infoScreen: true` must NOT be auto-transitioned — it has to
 * render as a static screen (title/description/infoImage) and wait for the
 * member's own Continue press. Read straight off the payload rather than
 * folded into `isGateNode` itself: every OTHER caller of `isGateNode` (the
 * hop-cap error message, the instant-skip bookkeeping) still needs to know
 * "is this structurally a gate" regardless of the flag, so the flag is its
 * own check the auto-advance walk ANDs in separately.
 */
function isFlaggedInfoGate(session: DynamicSession): boolean {
  return session.uiPayload?.infoScreen === true;
}

/** Fallback ack key for a gate node that carries no expression element.
 * `isGateNode` only returns true for an `info` node or a `question` node with
 * expression elements, so this fallback is reached ONLY for an elementless
 * `info` gate — for which we ack with the legacy `_gate` key. If the backend
 * never emits an elementless `info` gate this branch is dead; keeping it (and
 * this note) is cheap insurance until that shape is confirmed.
 * TODO(dynamic-assessment): confirm whether elementless `info` gates exist. */
const DEFAULT_GATE_ACK_KEY = '_gate';

/**
 * The answers payload required when auto-advancing past a gate (expression) node.
 * Each expression element's `name` is registered as an `allowed_signal_key` on
 * the server, so the ack must send THAT exact key with an empty value — sending
 * `{}` raises `answers_required`, and sending the wrong key raises
 * `answer_unknown_key`. Gate nodes don't all use the same key: `start` carries
 * `_gate`, but `score_route` (phq_gad_v1) carries `_score`. So derive the key(s)
 * from the node's own expression elements rather than hard-coding `_gate`; fall
 * back to `_gate` only for an elementless `info` node.
 *
 * Takes the `uiPayload` directly (not the whole `DynamicSession`) so a
 * FLAGGED gate's Continue handler (`continueGate` below) can derive the exact
 * same ack from `content.uiPayload` — the only piece of the session it still
 * has once the gate has been sitting on screen, possibly for a while.
 */
function gateAck(payload: DynamicUiPayload | null | undefined): Record<string, unknown> {
  const elements = payload?.elements ?? [];
  const keys = elements
    .filter(
      (el): el is Record<string, unknown> =>
        typeof el === 'object' &&
        el !== null &&
        (el as Record<string, unknown>).type === 'expression'
    )
    .map((el) => el.name)
    .filter((name): name is string => typeof name === 'string' && name.length > 0);
  if (keys.length === 0) return { [DEFAULT_GATE_ACK_KEY]: '' };
  return Object.fromEntries(keys.map((key) => [key, '']));
}

/**
 * Safety cap on consecutive gate auto-transitions within a single flow. A
 * well-formed flow walks through at most a couple of gates (start, maybe a
 * routing node) before reaching a question or terminal. A misconfigured flow
 * that keeps returning an active gate node would otherwise loop forever,
 * hammering the transition endpoint — so bail out (surfaced as `error`, which
 * hands off to the legacy fallback) once we exceed this.
 */
const MAX_GATE_HOPS = 10;

/**
 * The next flow to serve: the first configured `flowKey` (in the caller's order)
 * whose backend status is not terminal, skipping any we've already attempted in
 * the current pass. A flow the backend doesn't list can't be served, so it's
 * skipped too. Returns undefined when every configured flow is done.
 *
 * Reading status from the flows list is what lets us SKIP already-completed
 * screeners without starting a session for them — a resumed user who answered
 * the first two flows jumps straight to the third with no wasted POSTs.
 */
function pickNextFlow(
  flows: DynamicFlowItem[],
  flowKeys: readonly string[],
  attempted: Set<string>
): DynamicFlowItem | undefined {
  for (const key of flowKeys) {
    if (attempted.has(key)) continue;
    const flow = flows.find((f) => f.flowKey === key);
    if (!flow) continue;
    if (flow.status === 'completed' || flow.status === 'abandoned') continue;
    return flow;
  }
  return undefined;
}

/**
 * True when a session response shows the flow reached a TERMINAL: the server
 * reports `status: 'completed'`, or hands back a terminal node payload (the
 * wire shape varies — see DynamicSession.uiPayload).
 *
 * An `abandoned` session is deliberately NOT counted: an abandoned flow must
 * still fall back to its standalone screener rather than being treated as
 * served.
 */
function reachedTerminal(session: DynamicSession): boolean {
  if (session.status === 'abandoned') return false;
  return session.status === 'completed' || session.uiPayload?.nodeType === 'terminal';
}

/**
 * Extract a crisis-line outcome from a session response, wherever the server
 * placed it (session top level or the terminal node's ui_payload — see
 * DynamicTerminalOutcome). Undefined when the response carries none.
 *
 * The flag and the URNs are read INDEPENDENTLY from either location rather than
 * from a single chosen source: the wire shape isn't finalized (see the
 * DynamicTerminalOutcome TODO), so a mixed placement — e.g. `requiresCrisisLine`
 * on the ui_payload but `qualifiedProgramUrns` at the top level — must still
 * resolve. A crisis outcome needs the flag set (in either spot) AND at least one
 * URN (from either spot).
 */
function crisisOutcomeFrom(session: DynamicSession): string[] | undefined {
  const requiresCrisisLine =
    session.requiresCrisisLine === true || session.uiPayload?.requiresCrisisLine === true;
  if (!requiresCrisisLine) return undefined;
  const urns = session.qualifiedProgramUrns?.length
    ? session.qualifiedProgramUrns
    : session.uiPayload?.qualifiedProgramUrns;
  return urns && urns.length > 0 ? urns : undefined;
}

/**
 * Drives a set of dynamic-assessment flows one at a time, but lets the BACKEND
 * decide which still need answering.
 *
 * `flowKeys` defaults to `DYNAMIC_ONBOARDING_FLOW_KEYS` — the full onboarding
 * assessment region, in serve order. Pass a subset to serve fewer: a host app that only
 * wants one screener passes `['aom_screener_v1']` and everything else here works
 * unchanged (exhaustion fires as soon as that single flow completes or skips).
 * To add an assessment to the onboarding step, add its key to the constant; to serve a
 * different set elsewhere, pass it in.
 *
 * Each pass reads `/flows/` exactly once, up front, then walks flows locally:
 *   1. GET `/flows/` once for the per-flow status (pending / active / completed).
 *   2. `pickNextFlow` selects the first configured flow that still needs work,
 *      skipping completed/abandoned ones — so no session is started for a
 *      screener the user has already finished (the fix for the duplicate
 *      start/transition calls on resume).
 *   3. `startOrResumeSession` opens (pending) or resumes (active) that flow.
 *   4. Auto-transition through any gate nodes with `gateAck` (the node's own
 *      expression key) so the gate evaluates routing conditions server-side.
 *   5a. Session reached a terminal (instant skip) → loop back to (2), reusing
 *       the same `/flows/` snapshot (the just-attempted flow is excluded via
 *       `attempted`), no blank screen.
 *   5b. Session reached a `question` node → expose `content` for render.
 *   6. `submit(answers)` transitions the question, then triggers a new pass —
 *      which re-reads `/flows/` so the backend's updated state (and any
 *      reordering/insertion based on the answer) drives the next screen.
 */
export function useDynamicAssessmentSession(
  flowKeys: readonly string[] = DYNAMIC_ONBOARDING_FLOW_KEYS
): UseDynamicAssessmentSession {
  const apiClient = useDynamicAssessmentApiClient();
  const { trackEvent, logError } = useDynamicAssessmentAnalytics();
  const [pass, setPass] = useState(0);
  const [content, setContent] = useState<DynamicAssessmentContent | undefined>();
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isExhausted, setIsExhausted] = useState(false);
  const [completedFlowKeys, setCompletedFlowKeys] = useState<readonly string[]>([]);
  const [answeredFlows, setAnsweredFlows] = useState<readonly AnsweredFlow[]>([]);
  const [isResumed, setIsResumed] = useState(false);
  const [crisisProgramUrns, setCrisisProgramUrns] = useState<string[] | undefined>();
  const [error, setError] = useState<unknown>(null);

  /**
   * Flow keys THIS pass drove to a terminal, recorded from the session responses
   * (see `reachedTerminal`) rather than from the `/flows/` list. Unioned into
   * `completedFlowKeys` at exhaustion.
   *
   * Why it can't come from `/flows/` alone: that list is read ONCE at the top of
   * each pass, so a flow the client finishes LATER in the same pass is still
   * 'pending'/'active' in the snapshot the exhaustion branch filters. phq_gad_v1
   * is the live case — its last answer lands on the `score_route` gate, so the
   * gate walk below reaches `end_low_scores`/`end_crisis` after the snapshot was
   * taken. Filtering the snapshot alone reported the flow as NOT completed, and
   * the parent re-served the standalone `/step/phq-gad-questionnaire` — making
   * the member answer PHQ/GAD twice. Instant-skip terminals and a backend whose
   * `/flows/` status lags the session's own terminal are covered by the same
   * union. Accumulates for the lifetime of the mount, which is the pass.
   */
  const clientCompletedFlowsRef = useRef<Set<string>>(new Set());

  /**
   * Flows that actually rendered a question to the member during this mount,
   * mapped to the session it was rendered from.
   *
   * Read at every terminal to tell an ANSWERED screener from an instant-skipped
   * one: both reach a terminal and both land in `clientCompletedFlowsRef` (which
   * is a routing input and rightly counts either), but only the former is a
   * screener the member completed, which is what `answeredFlows` reports. The
   * session UUID is carried along because `content` has already moved on to the
   * next flow (or gone, at exhaustion) by the time the completion is reported.
   */
  const questionShownFlowsRef = useRef<Map<string, string>>(new Map());

  /**
   * `flowKeys` lands in the pass effect's dependencies, so an inline array from a
   * caller (`flowKeys={['aom_screener_v1']}`) would be a new identity every render
   * and re-run the pass forever. Key on the CONTENTS instead, so the effect only
   * re-runs when the set actually changes and callers don't have to memoize.
   *
   * Rebuilt FROM the joined string rather than returning `flowKeys` with a
   * narrowed dependency array. That older form needed an `exhaustive-deps`
   * suppression, and the suppression was quietly load-bearing in two ways:
   * a disabled React ESLint rule makes React Compiler skip optimizing this entire
   * hook, and if it ever did compile it, the compiler discards an identity-only
   * `useMemo` outright (`const stable = flowKeys`) — handing the effect the
   * caller's unstable array and restoring the very loop this guards against.
   * Deriving from the string makes `flowKeysKey` genuinely the only input, so the
   * deps are honest and neither hazard applies.
   *
   * JSON rather than a delimiter join. A `join('|')` round-trips wrong for any
   * key containing the delimiter (`['a|b']` comes back as `['a', 'b']`) — not a
   * risk for `DYNAMIC_ONBOARDING_FLOW_KEYS`, which are all `[a-z0-9_]`, but the
   * prop is `readonly string[]` and a host can pass anything. It also removes the
   * empty-array special case that a join needs: `[].join('|')` is `''` and
   * `''.split('|')` is `['']`, so an explicit empty `flowKeys` would smuggle in a
   * phantom key, whereas `JSON.parse('[]')` is just `[]`.
   */
  const flowKeysKey = JSON.stringify(flowKeys);
  const stableFlowKeys = useMemo(() => JSON.parse(flowKeysKey) as string[], [flowKeysKey]);

  /**
   * Append a flow to `answeredFlows` if the member was actually shown one of its
   * questions. Called at both terminal sites (submit response and gate walk),
   * since either can be the transition that finishes a flow. Idempotent, so a
   * flow reported terminal twice still yields one entry — the consumer emits one
   * analytics event per entry.
   */
  const recordAnsweredFlow = useCallback((flowKey: string) => {
    const sessionUuid = questionShownFlowsRef.current.get(flowKey);
    if (!sessionUuid) return;
    setAnsweredFlows((prev) =>
      prev.some((flow) => flow.flowKey === flowKey) ? prev : [...prev, { flowKey, sessionUuid }]
    );
  }, []);

  const submit = useCallback(
    async (answers: Record<string, unknown>) => {
      if (!content) return;
      try {
        // Submit-in-flight: keep the assessment mounted (busy button), not a full
        // spinner. The next pass's effect flips on isLoading and clears content.
        setIsSubmitting(true);
        const session = await transitionSession(
          apiClient,
          content.sessionUuid,
          answers,
          content.sessionVersion
        );
        // The transition that answers a flow's last question comes back as its
        // terminal — capture a crisis-line outcome (phq_gad_v1) before moving on.
        const crisisUrns = crisisOutcomeFrom(session);
        if (crisisUrns) setCrisisProgramUrns(crisisUrns);
        // Record the completion from the response itself, so exhaustion doesn't
        // depend on the next `/flows/` read having caught up (see
        // clientCompletedFlowsRef).
        if (reachedTerminal(session)) {
          clientCompletedFlowsRef.current.add(content.flowKey);
          recordAnsweredFlow(content.flowKey);
        }
        // Re-read /flows/ to find the next screener (the answer may have changed
        // which flows are pending).
        setPass((p) => p + 1);
      } catch (err) {
        // Surfaced via `error` → the screen hands off to the legacy flow. Log it
        // so the (otherwise silent) dynamic→legacy fallback is observable in Datadog.
        logError('dynamic-assessment submit failed; falling back to legacy screeners', {
          flowKey: content.flowKey,
          sessionUuid: content.sessionUuid,
          error: errorMessage(err),
        });
        trackEvent(`dynamic-assessment-submit-failed-${content.flowKey}`, {
          flowKey: content.flowKey,
          sessionUuid: content.sessionUuid,
          error: errorMessage(err),
        });
        setError(err);
        setIsSubmitting(false);
      }
    },
    [content, apiClient, trackEvent, logError, recordAnsweredFlow]
  );

  /**
   * Continue past a rendered gate — see the public type's doc comment. Reuses
   * `submit` wholesale rather than duplicating its transition/terminal/crisis
   * handling: the only thing specific to a gate is WHAT gets sent, and that's
   * exactly what `gateAck` already computes for the auto-advance walk above,
   * just fed `content.uiPayload` instead of a live `session`.
   */
  const continueGate = useCallback(async () => {
    if (!content) return;
    await submit(gateAck(content.uiPayload));
  }, [content, submit]);

  useEffect(() => {
    let cancelled = false;
    // Aborts the in-flight `/flows/` GET on unmount / a new pass. `cancelled`
    // alone only stops us ACTING on the response — the request itself still ran to
    // completion. Deliberately scoped to the GET: `DynamicAssessmentApiClient.post` takes no
    // config, and that asymmetry is intentional. The gate-walk transitions are
    // state-MUTATING, so letting one finish leaves the session on a known node,
    // where aborting mid-flight leaves it ambiguous; a resumed session then picks
    // up from wherever the last completed transition left it.
    const controller = new AbortController();

    (async () => {
      // The flow being processed when an error is thrown — for the Datadog log
      // context in the catch below (out of scope of the loop's `next`).
      let currentFlowKey: string | undefined;
      try {
        setIsLoading(true);
        setIsSubmitting(false);
        setError(null);
        setContent(undefined);

        // Flows attempted in THIS pass — guards the instant-skip loop below from
        // re-selecting a flow that didn't reach a question (and would otherwise
        // spin forever).
        const attempted = new Set<string>();

        // Read /flows/ ONCE per pass. The instant-skip loop below reuses this
        // snapshot and relies on `attempted` to skip flows it has already tried,
        // rather than re-fetching on every iteration: a flow completed by the
        // previous iteration's gate-walk is the flow we just attempted, so
        // `attempted` already excludes it — a fresh read wasn't buying anything.
        // Backend status changes driven by an ANSWER are still picked up, because
        // each submit bumps `pass` and re-runs this effect (a new fetch).
        const flows = await fetchDynamicFlows(apiClient, controller.signal);
        if (cancelled) return;

        // First read of the mount: any configured flow already `active` or
        // `completed` means an earlier page load started this assessment, so this
        // entry is a continuation. Derived here rather than from
        // `completedFlowKeys` (only populated at exhaustion, far too late for the
        // start events that need to carry it).
        if (pass === 0) {
          setIsResumed(
            flows.some(
              (flow) =>
                stableFlowKeys.includes(flow.flowKey) &&
                (flow.status === 'active' || flow.status === 'completed')
            )
          );
        }

        // Loop past completed/instant-skip flows until one yields a question or
        // none remain.
        while (!cancelled) {
          const next = pickNextFlow(flows, stableFlowKeys, attempted);
          if (!next) {
            // Capture which configured flows completed so the parent can decide
            // per-flow whether a standalone screener is still needed. Two
            // sources, unioned: the backend's `/flows/` statuses, plus the flows
            // THIS pass drove to a terminal (clientCompletedFlowsRef) — the
            // snapshot is read before those happen, so the list alone under-reports
            // them. Filtered to configured keys; an unpublished flow is in neither
            // source and an abandoned one in neither, so it's still never treated
            // as served (fail-safe).
            setCompletedFlowKeys(
              stableFlowKeys.filter(
                (key) =>
                  clientCompletedFlowsRef.current.has(key) ||
                  flows.some((f) => f.flowKey === key && f.status === 'completed')
              )
            );
            setIsExhausted(true);
            setIsLoading(false);
            return;
          }
          attempted.add(next.flowKey);
          currentFlowKey = next.flowKey;

          let session = await startOrResumeSession(apiClient, next.flowKey);

          // Auto-advance through gate nodes. Ack with the node's own expression
          // key(s) (via gateAck) instead of `{}` — the server registers each
          // expression element name as an allowed_signal_key and rejects empty
          // answers with `answers_required`, or the wrong key with
          // `answer_unknown_key` (e.g. score_route wants `_score`, not `_gate`).
          // Capped at MAX_GATE_HOPS so a misconfigured perpetually-active gate
          // can't spin forever.
          //
          // `!isFlaggedInfoGate(session)` stops the walk ON the gate instead of
          // past it when the node is flagged `infoScreen: true` — the ONLY
          // change auto-advance needs for a renderable gate. Everything else
          // (the structural isGateNode check, the hop cap, the elementless
          // `_gate` fallback in `gateAck`) is unchanged for a flagged gate; the
          // member's own Continue press (`continueGate` below) sends the exact
          // ack this loop would have sent, so routing is untouched either way.
          let gateHops = 0;
          while (
            session.status === 'active' &&
            isGateNode(session) &&
            !isFlaggedInfoGate(session)
          ) {
            if (cancelled) return;
            if (gateHops >= MAX_GATE_HOPS) {
              throw new Error(
                `Dynamic assessment flow "${next.flowKey}" exceeded ${MAX_GATE_HOPS} gate transitions`
              );
            }
            gateHops += 1;
            session = await transitionSession(
              apiClient,
              session.sessionUuid,
              gateAck(session.uiPayload),
              session.version
            );
          }
          if (cancelled) return;

          // A gate-walk can land straight on a terminal (e.g. a resumed session
          // whose last transition was interrupted) — capture a crisis outcome
          // here too, not just on submit.
          const crisisUrns = crisisOutcomeFrom(session);
          if (crisisUrns) setCrisisProgramUrns(crisisUrns);

          // The gate walk can finish the flow within THIS pass (phq_gad_v1's
          // score_route → end_low_scores/end_crisis, or an instant-skip
          // terminal). The `flows` snapshot above predates that, so record the
          // completion here — otherwise the exhaustion branch reports the flow as
          // unserved and the parent re-runs its standalone screener.
          if (reachedTerminal(session)) {
            clientCompletedFlowsRef.current.add(next.flowKey);
            recordAnsweredFlow(next.flowKey);
          }

          // Render either a real question OR a gate the walk above stopped ON
          // because it's flagged `infoScreen: true` — both are "something to
          // show the member" from this effect's perspective; the two render
          // completely differently downstream (DynamicAssessmentFlow branches
          // on `uiPayload.infoScreen`), but the hook's own bookkeeping doesn't
          // need to tell them apart. The flagged gate DOES face the member
          // (unlike a silent instant-skip), so it counts as "shown" the same
          // way a question does — its flow's eventual terminal is still an
          // answered screener, not an instant skip.
          const payload = session.uiPayload;
          if (payload && (payload.nodeType === 'question' || payload.infoScreen === true)) {
            questionShownFlowsRef.current.set(next.flowKey, session.sessionUuid);
            setContent({
              flowKey: next.flowKey,
              sessionUuid: session.sessionUuid,
              sessionVersion: session.version,
              uiPayload: payload,
            });
            setIsLoading(false);
            return;
          }

          // Completed (instant skip) or an unexpected non-question node → try the
          // next flow. `attempted` prevents re-selecting this same flow.
        }
      } catch (err) {
        if (!cancelled) {
          // Surfaced via `error` → the screen hands off to the legacy flow. Log it
          // so the (otherwise silent) dynamic→legacy fallback is observable in Datadog.
          logError('dynamic-assessment flow failed; falling back to legacy screeners', {
            flowKey: currentFlowKey,
            error: errorMessage(err),
          });
          trackEvent(`dynamic-assessment-load-failed-${currentFlowKey ?? 'unknown'}`, {
            flowKey: currentFlowKey,
            error: errorMessage(err),
          });
          setError(err);
          setIsLoading(false);
        }
      }
    })();

    return () => {
      // Aborting rejects the in-flight GET, and that rejection lands in the `catch`
      // above — which must NOT report it, or navigating away from the assessment would
      // log a Datadog fallback and a `-load-failed` counter for a pass nobody is
      // watching. The existing `!cancelled` guard already covers it: both statements
      // here run synchronously, while the rejection is handled a microtask later, so
      // `cancelled` is true by the time the catch sees it. (Their relative order is
      // therefore irrelevant — what matters is that both precede the microtask.)
      cancelled = true;
      controller.abort();
    };
    // `apiClient` is a stable instance from DynamicAssessmentProvider, and the analytics functions
    // are identity-stable by construction there (they delegate through a ref), so
    // an inline `analytics={{...}}` from a host can't retrigger this pass.
  }, [pass, apiClient, trackEvent, logError, stableFlowKeys, recordAnsweredFlow]);

  return {
    content,
    isLoading,
    isSubmitting,
    isExhausted,
    completedFlowKeys,
    answeredFlows,
    isResumed,
    crisisProgramUrns,
    error,
    submit,
    continueGate,
  };
}
